import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Plus, Pencil, Trash2, Loader2, X, Check, AlertCircle, Receipt } from "lucide-react";
import {
  getAdminActivationFees,
  createAdminActivationFee,
  updateAdminActivationFee,
  deleteAdminActivationFee,
  getCommercialCurrencies,
  AdminActivationFee,
  HardwareClass,
  CommercialCurrency,
} from "../../api/services/adminActivationFeeService";
import { getAdminTaxProfiles, AdminTaxProfile } from "../../api/services/adminTaxProfileService";
import { formatPrice, majorToMinorString, minorStringToMajor } from "@/utils/formatPrice";
import { getErrorMessage } from "@/utils/errorUtils";

const DEFAULT_COUNTRY = "DEFAULT";
const HARDWARE_CLASSES: HardwareClass[] = ["entry", "mid", "high"];

// Draft shape used by the form: fee is edited in MAJOR units; we convert to
// integer `fee_minor` on submit. Everything else maps 1:1 to the API record.
interface FeeDraft {
  _id?: string;
  country_code: string;
  currency: string;
  hardware_class: HardwareClass;
  feeMajor: string; // major-unit text input
  tax_profile_id: string;
  effective_from: string;
  effective_to: string;
}

const emptyDraft = (): FeeDraft => ({
  country_code: "",
  currency: "",
  hardware_class: "entry",
  feeMajor: "",
  tax_profile_id: "",
  effective_from: "",
  effective_to: "",
});

const toDraft = (f: AdminActivationFee): FeeDraft => ({
  _id: f._id,
  country_code: f.country_code,
  currency: f.currency,
  hardware_class: f.hardware_class,
  feeMajor: String(minorStringToMajor(f.fee_minor, f.currency)),
  tax_profile_id: f.tax_profile_id || "",
  effective_from: f.effective_from || "",
  effective_to: f.effective_to || "",
});

export default function ActivationFees() {
  const { t } = useTranslation();

  const [fees, setFees] = useState<AdminActivationFee[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Reference data for the form
  const [countries, setCountries] = useState<CommercialCurrency[]>([]);
  const [taxProfiles, setTaxProfiles] = useState<AdminTaxProfile[]>([]);

  // Filters
  const [filterCountry, setFilterCountry] = useState("");
  const [filterClass, setFilterClass] = useState<HardwareClass | "">("");

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<FeeDraft | null>(null);
  const [formErr, setFormErr] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<AdminActivationFee | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      setFees(
        await getAdminActivationFees({
          country_code: filterCountry || undefined,
          hardware_class: filterClass || undefined,
        })
      );
    } catch (e: unknown) {
      setErr(getErrorMessage(e) || t("admin.activationfees.errors.load", { defaultValue: "Failed to load activation fees" }));
    } finally {
      setLoading(false);
    }
  };

  // Reload the list whenever filters change.
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCountry, filterClass]);

  // Load reference data once.
  useEffect(() => {
    (async () => {
      try {
        setCountries(await getCommercialCurrencies());
      } catch {
        // Non-fatal: admin can still type a currency manually.
      }
      try {
        setTaxProfiles(await getAdminTaxProfiles());
      } catch {
        // Non-fatal: tax profile is optional.
      }
    })();
  }, []);

  const countryOptions = useMemo(() => {
    const codes = countries.map((c) => c.country_code).filter(Boolean);
    return [DEFAULT_COUNTRY, ...codes];
  }, [countries]);

  const currencyForCountry = (code: string): string => {
    const match = countries.find((c) => c.country_code === code);
    return match?.currency || "";
  };

  const taxProfileLabel = (id?: string | null): string => {
    if (!id) return t("admin.activationfees.table.noTaxProfile", { defaultValue: "—" });
    const p = taxProfiles.find((tp) => tp._id === id);
    return p ? `${p.label} (${p.country_code})` : id;
  };

  const openNew = () => {
    setFormErr("");
    setEditing(emptyDraft());
    setModalOpen(true);
  };

  const openEdit = (f: AdminActivationFee) => {
    setFormErr("");
    setEditing(toDraft(f));
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setFormErr("");
  };

  // When the admin picks a country, prefill the currency from the configured
  // country/currency matrix (still editable). Satisfies "currency comes from
  // the record/config, never hardcoded".
  const onCountryChange = (code: string) => {
    if (!editing) return;
    const currency = code === DEFAULT_COUNTRY ? editing.currency : currencyForCountry(code) || editing.currency;
    setEditing({ ...editing, country_code: code, currency });
  };

  // Map backend errors to clear, translated messages. The backend surfaces:
  //  - 400 overlap/duplicate ("already covers this country, hardware class…")
  //  - 409 FEE_NOT_CONFIGURED
  //  - 404 not found
  const mapError = (e: unknown): string => {
    const errObj = e as {
      response?: { status?: number; data?: { code?: string; message?: string } };
      message?: string;
    };
    const status = errObj?.response?.status;
    const code = errObj?.response?.data?.code;
    const apiMsg = errObj?.response?.data?.message;

    if (code === "FEE_NOT_CONFIGURED" || status === 409) {
      return t("admin.activationfees.errors.notConfigured", {
        defaultValue: "No activation fee is configured for this country and hardware class.",
      });
    }
    if (status === 400) {
      // Overlap / duplicate are the common 400s; prefer the backend message but
      // give a clear fallback.
      if (apiMsg && /overlap|already|duplicate|covers/i.test(apiMsg)) {
        return t("admin.activationfees.errors.overlap", {
          defaultValue:
            "A fee schedule already overlaps this country, hardware class and effective period. Adjust the dates or edit the existing one.",
        });
      }
      return apiMsg || t("admin.activationfees.errors.invalid", { defaultValue: "Some fields are invalid. Please check and try again." });
    }
    if (status === 404) {
      return t("admin.activationfees.errors.notFound", { defaultValue: "This activation fee no longer exists." });
    }
    return apiMsg || getErrorMessage(e) || t("admin.activationfees.errors.save", { defaultValue: "Failed to save activation fee" });
  };

  const save = async () => {
    if (!editing) return;
    setFormErr("");

    const feeMajorNum = Number(editing.feeMajor);
    if (!editing.country_code) {
      setFormErr(t("admin.activationfees.errors.countryRequired", { defaultValue: "Country is required" }));
      return;
    }
    if (!editing.currency || !/^[A-Za-z]{3}$/.test(editing.currency)) {
      setFormErr(t("admin.activationfees.errors.currencyRequired", { defaultValue: "A valid 3-letter currency is required" }));
      return;
    }
    if (editing.feeMajor === "" || !Number.isFinite(feeMajorNum) || feeMajorNum < 0) {
      setFormErr(t("admin.activationfees.errors.feeRequired", { defaultValue: "Fee must be zero or a positive amount" }));
      return;
    }

    const payload: Partial<AdminActivationFee> = {
      country_code: editing.country_code.toUpperCase(),
      currency: editing.currency.toUpperCase(),
      hardware_class: editing.hardware_class,
      // Convert the major-unit input to integer minor units for the API.
      fee_minor: majorToMinorString(feeMajorNum, editing.currency),
      tax_profile_id: editing.tax_profile_id || null,
      effective_from: editing.effective_from ? new Date(editing.effective_from).toISOString() : undefined,
      effective_to: editing.effective_to ? new Date(editing.effective_to).toISOString() : null,
    };

    setSaving(true);
    try {
      if (editing._id) await updateAdminActivationFee(editing._id, payload);
      else await createAdminActivationFee(payload);
      closeModal();
      await load();
    } catch (e: unknown) {
      setFormErr(mapError(e));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete?._id) return;
    setBusyId(confirmDelete._id);
    try {
      await deleteAdminActivationFee(confirmDelete._id);
      setConfirmDelete(null);
      await load();
    } catch (e: unknown) {
      setErr(mapError(e));
    } finally {
      setBusyId(null);
    }
  };

  const formatFee = (f: AdminActivationFee) => formatPrice(Number(f.fee_minor), f.currency);

  const fld = "w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm";

  const hardwareLabel = (hc: HardwareClass) =>
    t(`admin.activationfees.hardware.${hc}`, { defaultValue: hc.charAt(0).toUpperCase() + hc.slice(1) });

  const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : "—");

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Receipt className="w-6 h-6" /> {t("admin.activationfees.title", { defaultValue: "Activation Fees" })}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {t("admin.activationfees.description", {
              defaultValue: "Set one-time BYO activation fees per country and hardware class.",
            })}
          </p>
        </div>
        <button
          onClick={openNew}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-medium whitespace-nowrap"
        >
          <Plus className="w-4 h-4" /> {t("admin.activationfees.button.add", { defaultValue: "Add Fee" })}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-500 mb-1">
            {t("admin.activationfees.filter.country", { defaultValue: "Country" })}
          </label>
          <select className={fld} value={filterCountry} onChange={(e) => setFilterCountry(e.target.value)}>
            <option value="">{t("admin.activationfees.filter.allCountries", { defaultValue: "All countries" })}</option>
            {countryOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-500 mb-1">
            {t("admin.activationfees.filter.hardware", { defaultValue: "Hardware class" })}
          </label>
          <select
            className={fld}
            value={filterClass}
            onChange={(e) => setFilterClass(e.target.value as HardwareClass | "")}
          >
            <option value="">{t("admin.activationfees.filter.allClasses", { defaultValue: "All classes" })}</option>
            {HARDWARE_CLASSES.map((hc) => (
              <option key={hc} value={hc}>
                {hardwareLabel(hc)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {err && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg px-3 py-2">
          <AlertCircle className="w-4 h-4 shrink-0" /> {err}
        </div>
      )}

      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t("admin.activationfees.loading", { defaultValue: "Loading activation fees…" })}
          </div>
        ) : fees.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            {t("admin.activationfees.empty", { defaultValue: "No activation fees yet. Click Add Fee to create one." })}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-gray-500 border-b border-gray-200 dark:border-gray-800">
                <tr>
                  <th className="px-4 py-3 font-medium">{t("admin.activationfees.table.country", { defaultValue: "Country" })}</th>
                  <th className="px-4 py-3 font-medium">{t("admin.activationfees.table.hardware", { defaultValue: "Hardware" })}</th>
                  <th className="px-4 py-3 font-medium">{t("admin.activationfees.table.fee", { defaultValue: "Fee" })}</th>
                  <th className="px-4 py-3 font-medium">{t("admin.activationfees.table.taxProfile", { defaultValue: "Tax Profile" })}</th>
                  <th className="px-4 py-3 font-medium">{t("admin.activationfees.table.effective", { defaultValue: "Effective" })}</th>
                  <th className="px-4 py-3 font-medium text-right">{t("admin.activationfees.table.actions", { defaultValue: "Actions" })}</th>
                </tr>
              </thead>
              <tbody>
                {fees.map((f) => (
                  <tr key={f._id} className="border-b border-gray-100 dark:border-gray-800/60">
                    <td className="px-4 py-3">
                      <span className="uppercase font-medium text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">{f.country_code}</span>
                    </td>
                    <td className="px-4 py-3">{hardwareLabel(f.hardware_class)}</td>
                    <td className="px-4 py-3 font-medium">{formatFee(f)}</td>
                    <td className="px-4 py-3 text-gray-500">{taxProfileLabel(f.tax_profile_id)}</td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {fmtDate(f.effective_from)} → {f.effective_to ? fmtDate(f.effective_to) : t("admin.activationfees.table.open", { defaultValue: "open" })}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => openEdit(f)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => setConfirmDelete(f)} className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {modalOpen && editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closeModal}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-800">
              <h2 className="text-lg font-semibold">
                {editing._id
                  ? t("admin.activationfees.modal.editTitle", { defaultValue: "Edit Activation Fee" })
                  : t("admin.activationfees.modal.newTitle", { defaultValue: "New Activation Fee" })}
              </h2>
              <button onClick={closeModal} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {formErr && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg px-3 py-2">
                  <AlertCircle className="w-4 h-4 shrink-0" /> {formErr}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t("admin.activationfees.form.country", { defaultValue: "Country" })}
                  </label>
                  <select className={fld} value={editing.country_code} onChange={(e) => onCountryChange(e.target.value)}>
                    <option value="">{t("admin.activationfees.form.selectCountry", { defaultValue: "Select…" })}</option>
                    {countryOptions.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t("admin.activationfees.form.currency", { defaultValue: "Currency (ISO 4217)" })}
                  </label>
                  <input
                    className={fld}
                    value={editing.currency}
                    onChange={(e) => setEditing({ ...editing, currency: e.target.value.toUpperCase() })}
                    placeholder="e.g. NGN, XOF"
                    maxLength={3}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t("admin.activationfees.form.hardware", { defaultValue: "Hardware class" })}
                  </label>
                  <select
                    className={fld}
                    value={editing.hardware_class}
                    onChange={(e) => setEditing({ ...editing, hardware_class: e.target.value as HardwareClass })}
                  >
                    {HARDWARE_CLASSES.map((hc) => (
                      <option key={hc} value={hc}>
                        {hardwareLabel(hc)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t("admin.activationfees.form.fee", { defaultValue: "Fee amount" })}
                  </label>
                  <input
                    type="number"
                    min={0}
                    step="any"
                    className={fld}
                    value={editing.feeMajor}
                    onChange={(e) => setEditing({ ...editing, feeMajor: e.target.value })}
                    placeholder="e.g. 5000"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {t("admin.activationfees.form.feeHint", {
                      defaultValue: "Enter the amount in {{currency}}. Stored as exact minor units.",
                      currency: editing.currency || "local currency",
                    })}
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t("admin.activationfees.form.taxProfile", { defaultValue: "Tax profile (optional)" })}
                </label>
                <select
                  className={fld}
                  value={editing.tax_profile_id}
                  onChange={(e) => setEditing({ ...editing, tax_profile_id: e.target.value })}
                >
                  <option value="">{t("admin.activationfees.form.noTaxProfile", { defaultValue: "None" })}</option>
                  {taxProfiles.map((tp) => (
                    <option key={tp._id} value={tp._id}>
                      {tp.label} ({tp.country_code})
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t("admin.activationfees.form.effectiveFrom", { defaultValue: "Effective from" })}
                  </label>
                  <input
                    type="datetime-local"
                    className={fld}
                    value={editing.effective_from ? new Date(editing.effective_from).toISOString().slice(0, 16) : ""}
                    onChange={(e) => setEditing({ ...editing, effective_from: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t("admin.activationfees.form.effectiveTo", { defaultValue: "Effective to" })}
                  </label>
                  <input
                    type="datetime-local"
                    className={fld}
                    value={editing.effective_to ? new Date(editing.effective_to).toISOString().slice(0, 16) : ""}
                    onChange={(e) => setEditing({ ...editing, effective_to: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-200 dark:border-gray-800">
              <button onClick={closeModal} className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-700">
                {t("admin.activationfees.button.cancel", { defaultValue: "Cancel" })}
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}{" "}
                {t("admin.activationfees.button.save", { defaultValue: "Save" })}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setConfirmDelete(null)}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-sm shadow-xl p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-2">
              {t("admin.activationfees.delete.title", { defaultValue: "Delete this activation fee?" })}
            </h3>
            <p className="text-sm text-gray-500 mb-5">
              {t("admin.activationfees.delete.body", {
                defaultValue: "This removes the fee schedule. Registrations already charged keep their recorded amount.",
              })}
            </p>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-700">
                {t("admin.activationfees.button.cancel", { defaultValue: "Cancel" })}
              </button>
              <button
                onClick={doDelete}
                disabled={busyId === confirmDelete._id}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              >
                {busyId === confirmDelete._id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}{" "}
                {t("admin.activationfees.button.delete", { defaultValue: "Delete" })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
